/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';
import type { ReactNode } from 'react';

import Imgix from 'js/components/Imgix';
import redirect from 'js/lib/coursera.redirect';

import { Divider, Grid, Typography, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useEventingMetadata } from 'bundles/unified-career-academy/contexts/EventingMetadataContext';
import type { Asset } from 'bundles/unified-career-academy/types';

import _t from 'i18n!nls/career-academy';

export type CareerCardModuleProps = {
  roleSlug?: string | null;
  title?: string | null;
  image?: Asset;
  url?: string | null;
  description?: ReactNode;
  skills?: ReactNode;
  careerStats?: ReactNode;
  credentials?: ReactNode;
};

const getStyle: (canSelectCard: boolean) => Record<string, Interpolation<Theme>> = (canSelectCard) => ({
  trackedDivWrapper: css`
    height: 100%;
  `,

  cardWrapper: () => css`
    height: 100%;
    border: 1px solid var(--cds-color-grey-50);
    box-shadow: 0 0 4px rgb(0 0 0 / 25%);
    border-radius: 6px;
    align-content: flex-start;
    cursor: ${canSelectCard ? 'pointer' : 'auto'};
    max-width: 650px;
    overflow: hidden;
  `,

  cardWrapperLink: () => css`
    height: 100%;
    border: 1px solid var(--cds-color-grey-50);
    box-shadow: 0 0 4px rgb(0 0 0 / 25%);
    border-radius: 6px;
    align-content: flex-start;
    cursor: ${canSelectCard ? 'pointer' : 'auto'};
    max-width: 650px;
    overflow: hidden;
    transition: box-shadow 250ms ease-out, transform 250ms ease-out;

    &:hover {
      box-shadow: 0 0 17px rgb(0 0 0 / 25%);
      transform: scale(1.01);
    }
  `,

  image: css`
    width: 100%;
  `,

  content: () => css`
    margin: var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-200);
  `,

  skillsCTA: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-100);
    font-weight: 600;
  `,

  skills: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,

  divider: () => css`
    margin: 0 0 var(--cds-spacing-200);
  `,

  markdown: css`
    white-space: pre-wrap;
  `,

  partnerLogo: () => css`
    display: inline-block;
    border: 1px solid var(--cds-color-grey-200);
    border-radius: 4px;
    height: 40px;
    width: 40px;
    padding: var(--cds-spacing-50);
    margin-right: var(--cds-spacing-150);

    img {
      width: 100%;
    }
  `,

  partnerInfo: css`
    align-items: center;
  `,
});

const CareerCardModule = ({
  roleSlug,
  title,
  image,
  description,
  skills,
  careerStats,
  credentials,
  url,
}: CareerCardModuleProps) => {
  const eventingMetadata = useEventingMetadata();
  const track = useTracker();

  const careerCardEventingV3Data = {
    role: {
      roleSlug: roleSlug ?? '',
    },
  };

  const careerCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_role_card',
    careerCardEventingV3Data
  );

  const trackClick = () => {
    track('click_role_card', careerCardEventingV3Data);
  };

  const style = getStyle(!!url);

  const onSelectCard = () => {
    if (!url) {
      return;
    }

    trackClick();
    redirect.setLocation(url);
  };

  return (
    <TrackedDiv
      trackingName="career_card"
      data={{ title, ...eventingMetadata }}
      trackClicks
      withVisibilityTracking
      css={style.trackedDivWrapper}
      role={url ? 'button' : 'presentation'}
      onKeyPress={(event) => {
        if (event.key === 'Enter') {
          onSelectCard();
        }
      }}
      tabIndex={url ? 0 : -1}
    >
      <Grid
        container
        ref={careerCardRef}
        css={url ? style.cardWrapperLink : style.cardWrapper}
        data-testid="ca-career-card"
        onClick={onSelectCard}
      >
        <Grid item>{image?.url && <Imgix css={style.image} src={image.url} alt={image?.description || ''} />}</Grid>
        <Grid item css={style.content}>
          <Typography variant="h1semibold" component="h2">
            {title}
          </Typography>
          {description}
          <Typography2 component="p" css={style.skillsCTA}>
            {_t('Great if you like:')}
          </Typography2>
          <Grid item>{skills}</Grid>
          {careerStats && (
            <React.Fragment>
              <Divider color="light" css={style.divider} />
              <Grid item>{careerStats}</Grid>
            </React.Fragment>
          )}
          <Divider color="light" css={style.divider} />
          <Grid item>{credentials}</Grid>
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};

export default CareerCardModule;
